/**
 * Planning spend across the categories that share one cap.
 *
 * SmartBuy's hotels and flights draw on one 15,000-point allowance at
 * different rates, so "how much can I still spend" has no single answer: it
 * depends on the mix. Given what you plan to spend where, this says how much
 * of the cap that uses, what is left, and how much more each category could
 * then take.
 *
 * The sums are the engine's own, not a smooth rate: points are awarded on
 * whole units of spend (₹150 for HDFC), rounded down per transaction, and a
 * bonus is the rule's points less the base rate's. So the figures are exact
 * for a spend made as one transaction — splitting it over several can round
 * away a little more.
 *
 * Pure, and shared with the web app, so the sums shown are the ones tested.
 */

export interface RateSpec {
  rate: number;
  unitAmount: number;
  roundDown: boolean;
}

/** How a rule's spend draws on its cap. */
export type CapDraw =
  | { kind: "SPEND" }
  | { kind: "POINTS"; appliesTo: "ALL" | "BONUS"; rule: RateSpec; base: RateSpec | null };

function points(r: RateSpec, spend: number): number {
  if (r.unitAmount <= 0) return 0;
  const units = r.roundDown ? Math.floor(spend / r.unitAmount) : spend / r.unitAmount;
  return Math.round(units * r.rate * 100) / 100;
}

/** How much of the cap one transaction of this size uses. */
export function capUsedBy(draw: CapDraw, spend: number): number {
  if (spend <= 0) return 0;
  if (draw.kind === "SPEND") return spend;
  const own = points(draw.rule, spend);
  if (draw.appliesTo === "ALL") return own;
  return Math.max(0, Math.round((own - (draw.base ? points(draw.base, spend) : 0)) * 100) / 100);
}

/**
 * The smallest whole-rupee spend, in one transaction, that uses `amount` of
 * the cap — the point past which that category earns no more from it.
 * Infinity when spend here never uses the cap.
 */
export function spendToUse(draw: CapDraw, amount: number): number {
  if (amount <= 0) return 0;
  if (draw.kind === "SPEND") return Math.ceil(amount);
  const ceiling = 1_000_000_000;
  if (capUsedBy(draw, ceiling) < amount) return Infinity;
  let lo = 0;
  let hi = ceiling;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (capUsedBy(draw, mid) >= amount) hi = mid;
    else lo = mid + 1;
  }
  return lo;
}

export interface PlanLine {
  key: string;
  /** How spend here draws on the cap. Null: it does not. */
  draw: CapDraw | null;
  /** A sub-cap inside the cap this line also draws on, and what is left of it. */
  sub?: { key: string; remaining: number } | null;
}

export interface PlanResult {
  /** Cap the plan uses, and what is left of it afterwards. */
  used: number;
  left: number;
  /** How far the plan goes past the cap, in the cap's units. 0 when it fits. */
  over: number;
  lines: {
    key: string;
    planned: number;
    usesCap: number;
    /** Spend, in one more transaction, that would use up what the plan leaves. Null: no limit. */
    roomAfter: number | null;
    /** How far the plan goes past the line's sub-cap, in the cap's units; that part earns no bonus. */
    overSub: number;
  }[];
}

/** Each planned amount is taken as one transaction in its category. */
export function planCap(remaining: number, lines: PlanLine[], planned: Record<string, number>): PlanResult {
  // A line with a sub-cap uses it first, and draws on the shared cap only what
  // fits inside it — past its sub-cap a spend earns no bonus at all.
  const subLeft = new Map<string, number>();
  const uses = lines.map((l) => {
    const spend = Math.max(0, planned[l.key] ?? 0);
    let usesCap = l.draw ? capUsedBy(l.draw, spend) : 0;
    let overSub = 0;
    if (l.sub) {
      const room = subLeft.get(l.sub.key) ?? l.sub.remaining;
      const take = Math.min(usesCap, room);
      overSub = round2(usesCap - take);
      usesCap = round2(take);
      subLeft.set(l.sub.key, room - take);
    }
    return { line: l, planned: spend, usesCap, overSub };
  });
  const used = round2(uses.reduce((sum, u) => sum + u.usesCap, 0));
  const left = round2(Math.max(0, remaining - used));
  return {
    used,
    left,
    over: round2(Math.max(0, used - remaining)),
    lines: uses.map((u) => {
      // It stops at whichever runs out first: the shared cap or its own.
      const own = u.line.sub ? subLeft.get(u.line.sub.key) ?? u.line.sub.remaining : Infinity;
      const room = u.line.draw ? spendToUse(u.line.draw, Math.min(left, own)) : Infinity;
      return {
        key: u.line.key,
        planned: u.planned,
        usesCap: u.usesCap,
        roomAfter: Number.isFinite(room) ? room : null,
        overSub: u.overSub,
      };
    }),
  };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
